// REGISTER SCREENING JOB ******************************************************
// pathname: /v1/bestie-screening/{jobSlug}/register
// ************************************************************************************************
import useSWRMutation from 'swr/mutation';
import { fetcherSWRMutation } from '@utils/data-fetching/fetcher';
import { ApiSchema } from '@type/api/apiUtilityFn';

interface UseSwrRegisterScreeningJobProps {
  onSuccess?: () => void | Promise<void>;
  onError?: (err: unknown) => void;
}

interface RegisterJobPayload {
  jobSlug: string;
  data: ApiSchema<'ScreeningJobRequest'>;
}

/**
 * @description SWR Mutation: Register a screening job on the main backend.
 * Now accepts jobSlug dynamically when calling trigger({ jobSlug, data }).
 * @param props - { UseSwrRegisterScreeningJobProps } - Optional callbacks.
 */
export const useSwrRegisterScreeningJob = (props?: UseSwrRegisterScreeningJobProps) => {
  const { onSuccess, onError } = props ?? {};

  const {
    data, error, isMutating, reset, trigger: rawTrigger,
  } = useSWRMutation(
    // Use a static key - the URL will be dynamic based on the argument
    '/v1/bestie-screening/register',
    async (_, { arg }: { arg: RegisterJobPayload }) => {
      const { jobSlug, data: payload } = arg;
      const url = `/v1/bestie-screening/${jobSlug}/register`;

      return fetcherSWRMutation<void>(
        [url, 'ro', {
          method: 'POST',
          stringifyPayload: true,
          headers: {
            'Content-Type': 'application/json',
          },
        }],
        { arg: payload },
      );
    },
    {
      onSuccess: () => { if (onSuccess) { void onSuccess(); } },
      onError: (err) => { if (onError) onError(err); },
    },
  );

  // Wrapper for trigger to provide better API
  const trigger = async (payload: RegisterJobPayload) => rawTrigger(payload);

  return {
    data,
    error,
    isMutating,
    reset,
    trigger,
  };
};
